import { API_ORIGIN } from "../config/api";
import { refreshAuthToken } from "./cognito";

const DATA_HOSTS = new Set([
  "d1ct3zodbaiefe.cloudfront.net",
  "dd29pvm79j0ht.cloudfront.net",
  "d1dlav2y8bte65.cloudfront.net",
  "d8io2268y22ti.cloudfront.net",
]);
const S3_HOST = /^[a-z0-9][a-z0-9.-]+\.s3(\.[a-z0-9-]+)?\.amazonaws\.com$/;
const REFRESH_WINDOW_SECONDS = 120;

let refreshing = null;
const signedUrls = new Map();
let signedOwner = null;

const requestUrl = (input) => (typeof input === "string" ? input : input instanceof URL ? input.href : input?.url || "");

export function isBackendUrl(url) {
  return url.startsWith(`${API_ORIGIN}/`);
}

export function isProtectedDataUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && (DATA_HOSTS.has(parsed.hostname) || S3_HOST.test(parsed.hostname));
  } catch {
    return false;
  }
}

function expiresSoon(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !payload.exp || payload.exp - Date.now() / 1000 < REFRESH_WINDOW_SECONDS;
  } catch {
    return false;
  }
}

function refreshToken() {
  if (!refreshing) refreshing = refreshAuthToken().finally(() => { refreshing = null; });
  return refreshing;
}

async function currentToken() {
  const token = localStorage.getItem("mg_token");
  if (!token || !expiresSoon(token)) return token;
  try {
    return await refreshToken();
  } catch {
    return token;
  }
}

function withToken(input, init, token) {
  const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

export function createSecureFetch(nativeFetch) {
  async function backendFetch(input, init) {
    const token = await currentToken();
    const response = await nativeFetch(input, withToken(input, init, token));
    if (response.status !== 401 || !token) return response;
    let refreshed;
    try {
      refreshed = await refreshToken();
    } catch {
      return response;
    }
    return nativeFetch(input, withToken(input, init, refreshed));
  }

  function signedUrl(url) {
    const owner = localStorage.getItem("mg_token");
    if (owner !== signedOwner) {
      signedUrls.clear();
      signedOwner = owner;
    }
    const parsed = new URL(url);
    const objectUrl = `${parsed.origin}${parsed.pathname}`;
    const cached = signedUrls.get(objectUrl);
    if (cached && cached.until > Date.now()) return cached.promise;
    const entry = { until: Infinity };
    entry.promise = backendFetch(`${API_ORIGIN}/api/data/sign?url=${encodeURIComponent(objectUrl)}`, { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) {
          const error = new Error(`Data access denied (HTTP ${response.status})`);
          error.status = response.status;
          throw error;
        }
        const body = await response.json();
        entry.until = Date.now() + Math.min(60, Math.max(0, Number(body.expires_in || 0) - REFRESH_WINDOW_SECONDS)) * 1000;
        return body.url;
      })
      .catch((error) => {
        if (signedUrls.get(objectUrl) === entry) signedUrls.delete(objectUrl);
        throw error;
      });
    signedUrls.set(objectUrl, entry);
    return entry.promise;
  }

  return async function secureFetch(input, init) {
    const url = requestUrl(input);
    if (isBackendUrl(url)) return backendFetch(input, init);
    if (!isProtectedDataUrl(url)) return nativeFetch(input, init);
    let target;
    try {
      target = await signedUrl(url);
    } catch (error) {
      return new Response(null, { status: error.status || 503, statusText: error.message });
    }
    const options = { ...init };
    if (input instanceof Request) options.signal ||= input.signal;
    // Always revalidate with S3 (ETag): unchanged files come from the browser cache,
    // and a newly published file is downloaded immediately.
    if (!options.cache || options.cache === "default" || options.cache === "force-cache") options.cache = "no-cache";
    return nativeFetch(target, options);
  };
}

export function installSecureFetch(target = window) {
  if (!target.fetch || target.fetch.mgSecure) return;
  const secureFetch = createSecureFetch(target.fetch.bind(target));
  secureFetch.mgSecure = true;
  target.fetch = secureFetch;
}

export function clearSignedDataUrls() {
  signedUrls.clear();
  signedOwner = null;
}
